import React from "react";
import { useNavigate } from "react-router-dom";
import { getAccessToken, hasSession } from "../../services/api.ts";

const ResearchStudyDetail = ({ study }) => {
    const navigate = useNavigate();

    if (!study) return null;

    const {
        id,
        name,
        description,
        created_by_user,
        publication_url,
        datasets_info,
        ai_models_info,
        annotation_stats,
        labels,
        questions,
        status,
        created_at,
    } = study;

    const isAuthenticated = !!getAccessToken() || hasSession();

    const handleOpenStudy = () => {
        if (isAuthenticated) {
            navigate(`/Study/${id}`);
        } else {
            // Redirect to login with return URL
            navigate(`/login?redirect=/Study/${id}`);
        }
    };

    // Calculate total data stats
    const totalPatients = datasets_info?.reduce((acc, d) => acc + (d.patient_count || 0), 0) || 0;
    const totalImages = datasets_info?.reduce((acc, d) => acc + (d.image_count || 0), 0) || 0;
    const totalDocuments = datasets_info?.reduce((acc, d) => acc + (d.document_count || 0), 0) || 0;

    // Get unique anatomies and modalities
    const allAnatomies = [...new Set(datasets_info?.flatMap(d => d.anatomies || []) || [])];
    const allModalities = [...new Set(datasets_info?.flatMap(d => d.modalities || []) || [])];

    const formatDate = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric"
        });
    };

    return (
        <div className="study-detail-container">
            {/* Header */}
            <div className="study-detail-header">
                <h2 className="study-name">{name}</h2>
                <span className={`status-badge status-${status}`}>{status}</span>
            </div>

            {/* Creator Info */}
            <div className="study-creator">
                <i className="fa fa-user"></i>
                <span>
                    by {created_by_user?.first_name || created_by_user?.username || "Unknown"}
                </span>
                <span className="study-date">
                    {formatDate(created_at)}
                </span>
            </div>

            {/* Description */}
            {description && (
                <div className="study-section">
                    <h4 className="section-title">Description</h4>
                    <p className="study-description">{description}</p>
                </div>
            )}

            {/* Publication Link */}
            {publication_url && (
                <div className="study-section publication-section">
                    <h4 className="section-title">Publication</h4>
                    <a
                        href={publication_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="publication-link"
                    >
                        <i className="fa fa-external-link"></i>
                        View Publication
                    </a>
                </div>
            )}

            {/* Data Stats */}
            <div className="study-section">
                <h4 className="section-title">Data Overview</h4>
                <div className="stats-grid">
                    <div className="stat-item">
                        <span className="stat-value">{totalPatients}</span>
                        <span className="stat-label">Patients</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{totalImages}</span>
                        <span className="stat-label">Images</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{totalDocuments}</span>
                        <span className="stat-label">Documents</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{annotation_stats?.total_annotations || 0}</span>
                        <span className="stat-label">Annotations</span>
                    </div>
                </div>
            </div>

            {/* Datasets */}
            {datasets_info && datasets_info.length > 0 && (
                <div className="study-section">
                    <h4 className="section-title">Datasets ({datasets_info.length})</h4>
                    <div className="datasets-list">
                        {datasets_info.map((dataset, idx) => (
                            <div key={idx} className="dataset-item">
                                <span className="dataset-name">{dataset.name}</span>
                                <span className="dataset-stats">
                                    {dataset.patient_count} patients, {dataset.image_count} images
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Anatomy & Modality Tags */}
            {(allAnatomies.length > 0 || allModalities.length > 0) && (
                <div className="study-section tags-section">
                    {allAnatomies.length > 0 && (
                        <div className="tag-group">
                            <span className="tag-label">Anatomy:</span>
                            {allAnatomies.map((anatomy, idx) => (
                                <span key={idx} className="tag anatomy-tag">{anatomy}</span>
                            ))}
                        </div>
                    )}
                    {allModalities.length > 0 && (
                        <div className="tag-group">
                            <span className="tag-label">Modality:</span>
                            {allModalities.map((modality, idx) => (
                                <span key={idx} className="tag modality-tag">{modality}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* AI Models */}
            {ai_models_info && ai_models_info.length > 0 && (
                <div className="study-section">
                    <h4 className="section-title">AI Models Used</h4>
                    <div className="models-list">
                        {ai_models_info.map((model, idx) => (
                            <div
                                key={idx}
                                className="model-item"
                                style={{ borderLeftColor: model.color || "#6366f1" }}
                            >
                                <i
                                    className={`fa ${model.icon || "fa-robot"}`}
                                    style={{ color: model.color || "#6366f1" }}
                                ></i>
                                <span className="model-name">{model.name}</span>
                                <span className="model-type">{model.model_type_display || model.model_type}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Annotation Configuration */}
            {((labels && labels.length > 0) || (questions && questions.length > 0)) && (
                <div className="study-section">
                    <h4 className="section-title">Annotation Configuration</h4>
                    {labels && labels.length > 0 && (
                        <div className="annotation-config">
                            <span className="config-label">Labels:</span>
                            <div className="labels-list">
                                {labels.map((label, idx) => (
                                    <span
                                        key={idx}
                                        className="label-chip"
                                        style={{ backgroundColor: label.color || "#6366f1" }}
                                    >
                                        {label.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {questions && questions.length > 0 && (
                        <div className="annotation-config">
                            <span className="config-label">
                                <i className="fa fa-question-circle"></i>
                                {questions.length} question{questions.length > 1 ? "s" : ""} configured
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Open Study Button */}
            <div className="study-action-section">
                <button className="open-study-btn" onClick={handleOpenStudy}>
                    <i className="fa fa-play-circle"></i>
                    {isAuthenticated ? "Open Study" : "Login to Open Study"}
                </button>
                {!isAuthenticated && (
                    <p className="login-hint">
                        Sign in to explore the datasets and AI models
                    </p>
                )}
            </div>
        </div>
    );
};

export default ResearchStudyDetail;
